import { Restaurant, CuisineFilter } from './types';

declare const google: any;

const waitForGoogle = (): Promise<void> => {
  return new Promise((resolve) => {
    if (typeof google !== 'undefined' && google.maps && google.maps.places && google.maps.places.Place) {
      resolve();
      return;
    }
    const interval = setInterval(() => {
      if (typeof google !== 'undefined' && google.maps && google.maps.places && google.maps.places.Place) {
        clearInterval(interval);
        resolve();
      }
    }, 100);
  });
};

export const getCoordinatesFromZip = async (zipCode: string): Promise<{ lat: number; lng: number }> => {
  await waitForGoogle();
  return new Promise((resolve, reject) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: zipCode }, (results: any, status: any) => {
      if (status === 'OK' && results[0]) {
        resolve({
          lat: results[0].geometry.location.lat(),
          lng: results[0].geometry.location.lng(),
        });
      } else {
        reject(new Error('Could not find location for zip code'));
      }
    });
  });
};

const shuffleArray = <T>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const FAST_FOOD_KEYWORDS = [
  "mcdonalds", "burger king", "wendys", "taco bell", "chick-fil-a",
  "popeyes", "kfc", "subway", "dominos", "pizza hut", "little caesars",
  "sonic", "dairy queen", "jack in the box", "five guys", "shake shack",
  "whataburger", "hardees", "arbys", "panda express", "chipotle",
  "wingstop", "raising canes", "zaxbys", "cookout", "steak n shake",
  "checkers", "rallys", "del taco", "captain ds", "long john silvers",
];

const CUISINE_TYPE_MAP: Record<CuisineFilter, string[]> = {
  all: ['restaurant'],
  american: ['american_restaurant'],
  italian: ['italian_restaurant'],
  mexican: ['mexican_restaurant'],
  asian: ['chinese_restaurant', 'japanese_restaurant', 'korean_restaurant', 'thai_restaurant', 'vietnamese_restaurant', 'ramen_restaurant', 'sushi_restaurant'],
  pizza: ['pizza_restaurant'],
  seafood: ['seafood_restaurant'],
  barbecue: ['barbecue_restaurant'],
};

export const fetchNearbyRestaurants = async (
  zipCode: string,
  count: 10 | 20 | 30,
  cuisineFilter: CuisineFilter,
  excludeFastFood: boolean,
): Promise<Restaurant[]> => {
  await waitForGoogle();
  const { lat, lng } = await getCoordinatesFromZip(zipCode);

  const { Place, SearchNearbyRankPreference } = google.maps.places;

  const fetchCount = 20;
  const types = CUISINE_TYPE_MAP[cuisineFilter] || ['restaurant'];

  // For multi-type cuisine filters like asian, we fetch each type separately
  const allResults: any[] = [];

  for (const type of types) {
    const request = {
      fields: [
        'id',
        'displayName',
        'formattedAddress',
        'rating',
        'userRatingCount',
        'priceLevel',
        'regularOpeningHours',
        'photos',
        'types',
        'editorialSummary',
        'websiteURI',
        'googleMapsURI',
      ],
      locationRestriction: {
        center: new google.maps.LatLng(lat, lng),
        radius: 12000,
      },
      includedPrimaryTypes: [type],
      maxResultCount: fetchCount,
      rankPreference: SearchNearbyRankPreference.POPULARITY,
      language: 'en-US',
    };

    try {
      const { places } = await Place.searchNearby(request);
      if (places) allResults.push(...places);
    } catch (e) {
      // Some cuisine types may return no results - continue
    }
  }

  if (allResults.length === 0) {
    throw new Error('No restaurants found near that zip code');
  }

  // Deduplicate by place id
  const seen = new Set<string>();
  const unique = allResults.filter(p => {
    if (seen.has(p.id)) return false;
    seen.add(p.id);
    return true;
  });

  let filtered = unique.filter((place: any) =>
    place.rating && place.userRatingCount && place.userRatingCount > 50
  );

  if (excludeFastFood) {
    filtered = filtered.filter((place: any) => {
      const name = (place.displayName || '').toLowerCase();
      return !FAST_FOOD_KEYWORDS.some(kw => name.includes(kw));
    });
  }

  const shuffled = shuffleArray(filtered).slice(0, count);

  const restaurants: Restaurant[] = shuffled.map((place: any) => ({
    id: place.id,
    name: place.displayName,
    address: place.formattedAddress || '',
    rating: place.rating || 0,
    reviewCount: place.userRatingCount || 0,
    photoUrl: place.photos && place.photos.length > 0
      ? place.photos[0].getURI({ maxWidth: 800 })
      : '',
    cuisine: place.types
      ? place.types.find((t: string) => !['restaurant', 'food', 'point_of_interest', 'establishment'].includes(t)) || 'restaurant'
      : 'restaurant',
    priceLevel: place.priceLevel || 2,
    isOpen: place.regularOpeningHours?.openNow ?? true,
    summary: place.editorialSummary || '',
    websiteUrl: place.websiteURI || '',
    googleMapsUrl: place.googleMapsURI || '',
  }));

  if (restaurants.length === 0) {
    throw new Error('No restaurants found with those filters. Try adjusting your selection.');
  }

  return restaurants;
};